import { useState } from 'react';
import { motion } from 'framer-motion';
import { useProdutosPublico } from '@/hooks/useProdutos';
import { useCategorias } from '@/hooks/useCategorias';
import { Input } from '@/components/ui/Input';
import { ProductCard } from '@/components/loja/ProductCard';
import { Search, Package, Filter, X } from 'lucide-react';

export function Catalogo() {
  const [busca, setBusca] = useState('');
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string | null>(null);
  const { data: produtos, isLoading } = useProdutosPublico({ busca, categoriaId: categoriaSelecionada || undefined });
  const { data: categorias } = useCategorias();

  return (
    <div className="min-h-screen bg-gray-950 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Nosso <span className="text-orange-500">Catálogo</span>
          </h1>
          <p className="text-gray-400 max-w-2xl">
            Explore nossa linha completa de produtos impressos em 3D. 
            Qualidade premium, acabamento profissional.
          </p>
        </motion.div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
              <Input
                placeholder="Buscar produtos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-12 h-12 bg-gray-900 border-gray-800 text-white placeholder:text-gray-500 focus:border-orange-500"
              />
            </div>
            
            {/* Mobile filter indicator */}
            {categoriaSelecionada && (
              <button
                onClick={() => setCategoriaSelecionada(null)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500/10 text-orange-400 text-sm font-medium"
              >
                <Filter className="w-4 h-4" />
                Limpar filtros
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategoriaSelecionada(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                !categoriaSelecionada
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Todos
            </button>
            {categorias?.map((categoria: any) => (
              <button
                key={categoria.id}
                onClick={() => setCategoriaSelecionada(categoria.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  categoriaSelecionada === categoria.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {categoria.nome}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-gray-800 rounded-2xl mb-4" />
                <div className="h-4 bg-gray-800 rounded w-3/4 mb-2" />
                <div className="h-6 bg-gray-800 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : produtos && produtos.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {produtos.map((produto: any, index: number) => (
              <ProductCard key={produto.id} produto={produto} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Package className="mx-auto h-16 w-16 text-gray-600 mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">
              Nenhum produto encontrado
            </h3>
            <p className="text-gray-500">
              {busca 
                ? `Não encontramos produtos para "${busca}"` 
                : 'Nenhum produto disponível no momento'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
